import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';
import { CURRENT_SEASON } from '../core/current-season.js';
import { adminLeagueDataSurface, effectiveAdminLeagueId, getActiveLeagueId } from './league-context.js';

function numberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

Alpine.data('leagueTablePage', () => ({
  loading: true,
  refreshSpinning: false,
  firstLoad: true,
  error: '',
  season: CURRENT_SEASON,
  league: getActiveLeagueId('PDL'),
  availableSeasons: [CURRENT_SEASON],
  search: '',
  teams: [],
  _leagueSnap: '',

  async init() {
    this.league = effectiveAdminLeagueId(this.league || getActiveLeagueId('PDL'));
    this._leagueSnap = this.league;
    await this.loadSeason(this.season);
    this.firstLoad = false;

    this._onLeagueOrNav = () => {
      if (adminLeagueDataSurface() !== 'league-table') return;
      const next = effectiveAdminLeagueId('PDL');
      if (next === this._leagueSnap) return;
      this._leagueSnap = next;
      this.league = next;
      this.loadSeason(this.season);
    };
    document.addEventListener('pinecone:end', this._onLeagueOrNav);
    document.addEventListener('dm:admin-league-changed', this._onLeagueOrNav);
    this.$root?.addEventListener?.('alpine:destroy', () => {
      if (this._onLeagueOrNav) {
        document.removeEventListener('pinecone:end', this._onLeagueOrNav);
        document.removeEventListener('dm:admin-league-changed', this._onLeagueOrNav);
        this._onLeagueOrNav = null;
      }
    });
  },

  async loadSeason(nextSeason) {
    this.loading = true;
    this.error = '';
    this.season = String(nextSeason || CURRENT_SEASON);
    try {
      const [teamsRes, summaryRes] = await Promise.all([
        apiFetch(`/stats/teams?season=${encodeURIComponent(this.season)}&league=${encodeURIComponent(this.league)}`),
        apiFetch(`/stats/summary?season=${encodeURIComponent(this.season)}&league=${encodeURIComponent(this.league)}`),
      ]);
      const [teamsJson, summaryJson] = await Promise.all([
        teamsRes.json(),
        summaryRes.json(),
      ]);
      this.teams = (Array.isArray(teamsJson?.items) ? teamsJson.items : []).map((row, index) => {
        const legsWon = numberOrZero(row?.legsWon);
        const legsLost = numberOrZero(row?.legsLost);
        return {
          ...row,
          rank: index + 1,
          matches: numberOrZero(row?.matches),
          wins: numberOrZero(row?.wins),
          draws: numberOrZero(row?.draws),
          losses: numberOrZero(row?.losses),
          points: numberOrZero(row?.points),
          legsWon,
          legsLost,
          legsDiff: legsWon - legsLost,
        };
      });
      const seasons = Array.isArray(summaryJson?.availableSeasons) ? summaryJson.availableSeasons : [];
      this.availableSeasons = seasons.length ? seasons.map(String) : [this.season];
      if (!this.availableSeasons.includes(this.season)) {
        this.availableSeasons.unshift(this.season);
      }
    } catch (e) {
      console.error(e);
      this.error = 'Failed to load league table.';
      this.teams = [];
    } finally {
      this.loading = false;
      this.refreshSpinning = false;
    }
  },

  async manualRefresh() {
    this.refreshSpinning = true;
    await this.loadSeason(this.season);
  },

  async changeSeason(value) {
    await this.loadSeason(value);
  },

  rankTone(rank) {
    if (rank === 1) return 'bg-amber-400/20 text-amber-200 ring-1 ring-amber-300/30';
    if (rank === 2) return 'bg-slate-300/15 text-slate-200 ring-1 ring-slate-300/25';
    if (rank === 3) return 'bg-orange-400/20 text-orange-200 ring-1 ring-orange-300/30';
    return 'bg-white/5 text-neutral-200 ring-1 ring-white/10';
  },

  formToken(team) {
    return `${team.wins}-${team.draws}-${team.losses}`;
  },

  displayedTeams() {
    const q = String(this.search || '').trim().toLowerCase();
    const filtered = q
      ? this.teams.filter((team) => String(team?.teamName || '').toLowerCase().includes(q))
      : this.teams.slice();
    const sorted = filtered.toSorted((a, b) => {
      const points = numberOrZero(b?.points) - numberOrZero(a?.points);
      if (points) return points;
      const diff = numberOrZero(b?.legsDiff) - numberOrZero(a?.legsDiff);
      if (diff) return diff;
      return String(a?.teamName || '').localeCompare(String(b?.teamName || ''));
    });
    return sorted.map((team, index) => ({ ...team, rank: index + 1 }));
  },

  diffText(team) {
    const diff = numberOrZero(team?.legsDiff);
    return diff > 0 ? `+${diff}` : `${diff}`;
  },

  teamHref(teamId) {
    return `/admin/teams/${teamId}`;
  },
}));
